// ComboBox is a dropdown with a search box.
//
// A plain <select> can't be typed into, and once a catalog has dozens of suppliers,
// scrolling one is worse than typing three letters. This filters its options as you
// type (by each option's data-search text) and stores the chosen option's value in a
// hidden input, so the surrounding <form> submits the id — not the label.
//
// Island Architecture: a light-DOM web component wrapping a text input, a hidden input
// and a <ul> of options; the hidden input is what the form posts.

export class ComboBox extends HTMLElement {
  private input!: HTMLInputElement;
  private valueField!: HTMLInputElement;
  private list!: HTMLElement;
  private options: HTMLElement[] = [];
  private activeIndex = -1;
  // data-combo-free: la lista SUGIERE, no encierra. Lo que se escriba y no esté
  // en ella vale igual. Hace falta para campos que hoy son texto libre —un
  // acabado de perfil puede ser «RAL 7016» o «Madera roble»—: darles buscador no
  // puede costarles la posibilidad de escribir lo que no está en el catálogo.
  private free = false;

  connectedCallback(): void {
    const input = this.querySelector<HTMLInputElement>("[data-combo-input]");
    const hidden = this.querySelector<HTMLInputElement>("[data-combo-value]");
    const list = this.querySelector<HTMLElement>("[data-combo-list]");
    if (!input || !hidden || !list) return;

    this.input = input;
    this.valueField = hidden;
    this.list = list;
    this.free = this.hasAttribute("data-combo-free");
    this.options = Array.from(list.querySelectorAll<HTMLElement>("[data-combo-option], .combo__option"));

    // Reflect a preselected value (e.g. re-render after an error).
    const current = this.options.find((o) => o.dataset["value"] === hidden.value && hidden.value !== "");
    if (current) this.input.value = this.labelOf(current);
    // Un valor libre no está en la lista, así que se muestra tal cual.
    else if (this.free && hidden.value !== "") this.input.value = hidden.value;

    // Opens on typing (or ArrowDown), NOT on focus. The list floats over whatever sits
    // below the field — including the form's own submit button — so leaving it open just
    // because the box has focus turns a click on "Agregar" into picking an option.
    this.input.addEventListener("input", () => {
      // Typing means the previous pick no longer stands until one is chosen again.
      this.valueField.value = "";
      this.filter();
      this.open();
    });
    this.input.addEventListener("keydown", (e) => this.onKey(e));

    // Al enfocar se selecciona lo que hay, para que teclear REEMPLACE la opción
    // elegida en vez de añadirse a su nombre. Sin esto, escribir «bronce» sobre
    // un «Cristal crudo incoloro 6mm» busca «Cristal crudo incoloro 6mmbronce»,
    // que no coincide con nada: el buscador parece roto justo cuando ya había
    // algo elegido, que es casi siempre.
    this.input.addEventListener("focus", () => this.input.select());

    // Mousedown (not click) so the choice registers before the input's blur fires.
    this.list.addEventListener("mousedown", (e) => {
      const opt = (e.target as HTMLElement).closest<HTMLElement>("[data-combo-option], .combo__option");
      if (!opt) return;
      e.preventDefault();
      this.choose(opt);
    });

    this.input.addEventListener("blur", () => {
      // Let a pending option mousedown win the race, then close and tidy up.
      window.setTimeout(() => this.close(), 120);
    });
  }

  private labelOf(opt: HTMLElement): string {
    return (opt.textContent ?? "").trim();
  }

  private open(): void {
    this.filter();
    this.list.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
  }

  private close(): void {
    this.list.hidden = true;
    this.input.setAttribute("aria-expanded", "false");
    this.setActive(-1);

    // En modo libre lo escrito ES el valor: la lista sugiere y no encierra.
    if (this.free) {
      const escrito = this.input.value.trim();
      if (escrito !== this.valueField.value) {
        this.valueField.value = escrito;
        this.valueField.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return;
    }
    // If nothing is selected, don't leave stray text that looks like a choice.
    if (this.valueField.value === "") {
      const chosen = this.options.find((o) => o.dataset["value"] === "" && this.labelOf(o) === this.input.value.trim());
      if (!chosen) this.input.value = "";
    }
  }

  private filter(): void {
    const q = this.input.value.trim().toLowerCase();
    let firstVisible = -1;
    this.options.forEach((opt, i) => {
      const hay = opt.dataset["search"] ?? this.labelOf(opt).toLowerCase();
      // The empty "— none —" option always shows; the rest match the query.
      const match = q === "" || opt.dataset["value"] === "" || hay.includes(q);
      opt.hidden = !match;
      if (match && firstVisible === -1) firstVisible = i;
    });
    this.setActive(firstVisible);
  }

  private onKey(e: KeyboardEvent): void {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (this.list.hidden) this.open();
      this.move(e.key === "ArrowDown" ? 1 : -1);
    } else if (e.key === "Enter") {
      const active = this.options[this.activeIndex];
      if (!this.list.hidden && active) {
        e.preventDefault();
        this.choose(active);
      }
    } else if (e.key === "Escape") {
      this.close();
    }
  }

  private move(delta: number): void {
    const visible = this.options.filter((o) => !o.hidden);
    if (visible.length === 0) return;
    const curr = this.options[this.activeIndex];
    const at = curr ? visible.indexOf(curr) : -1;
    const next = visible[(at + delta + visible.length) % visible.length];
    if (next) this.setActive(this.options.indexOf(next));
  }

  private setActive(index: number): void {
    this.activeIndex = index;
    this.options.forEach((opt, i) => opt.classList.toggle("is-active", i === index));
    this.options[index]?.scrollIntoView({ block: "nearest" });
  }

  private choose(opt: HTMLElement): void {
    const antes = this.valueField.value;
    this.valueField.value = opt.dataset["value"] ?? "";
    this.input.value = opt.dataset["value"] ? this.labelOf(opt) : "";
    this.close();

    // Elegir una opción es CAMBIAR el valor del formulario, y hay que decirlo:
    // el campo oculto es el que el formulario envía, así que es el que tiene que
    // emitir el `change`. Sin esto un combo dentro de un formulario que se envía
    // a mano funciona igual —el valor está puesto—, pero uno cableado a HTMX con
    // `hx-trigger="change"` no dispara nunca: el valor cambia y no pasa nada,
    // que es la peor forma de fallar porque no hay error que leer.
    if (this.valueField.value !== antes) {
      this.valueField.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
}
